import { Dialog, Transition } from "@headlessui/react";
import { AlertTriangle } from "lucide-react";
import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";
import ReactSelector from "../common/CommonReactSelector";
import DeletePermission from "../suspence/DeletePermission";
import { toast } from "../../utils/toast";
import { getActivityListByProjectId } from "../../service/User";
import { getPermittedActivities } from "../../utils/activityPermissions";
import {
  getColumnAccessList,
  getColumnOptionsForAccess,
  grantColumnAccess,
  updateColumnAccess,
  deleteColumnAccess,
} from "../../service/TaskColumnAccess";

const USER_TYPE_VALUES = ["admin", "supervisor", "office_user"];
const applyTemplate = (template, vars = {}) =>
  String(template || "").replace(/\{(\w+)\}/g, (_, key) => (vars[key] ?? `{${key}}`));

const COLUMN_LABEL_KEYS = {
  taskId: "taskId",
  accountNo: "accno",
  Activity: "Activity",
  priority: "priority",
  completedDate: "comapletedDate",
  taskStatus: "taskstatus",
  source: "source",
  areaCode: "areCode",
  created_By: "created_By",
  createdDate: "createdatetime",
  assignedTo: "assignedTo",
  taskCategory: "taskCategory",
  comment: "comment",
};

const toReadableLabel = (key = "") =>
  String(key)
    .replace(/[_\-.]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

const extractColumnEntry = (item, index, t) => {
  if (typeof item === "string" || typeof item === "number") {
    const key = String(item).trim();
    return {
      id: `ungrouped|${key}|${index}`,
      key,
      label: t?.[COLUMN_LABEL_KEYS[key]] ?? toReadableLabel(key),
      group: "details",
      accessible: false,
    };
  }
  if (!item || typeof item !== "object") return null;
  const key = String(
    item.fieldName ??
      item.columnKey ??
      item.columnName ??
      item.value ??
      item.key ??
      item.name ??
      item.label ??
      ""
  ).trim();
  if (!key) return null;

  const labelCandidate = item.label ?? item.displayName ?? item.columnLabel;
  const label = labelCandidate ? String(labelCandidate).trim() : t?.[COLUMN_LABEL_KEYS[key]] ?? toReadableLabel(key);
  const group = String(item.group ?? "details").toLowerCase() === "standard" ? "standard" : "details";
  return {
    id: `${group}|${key}|${index}`,
    key,
    label,
    group,
    accessible: Boolean(item.accessible),
  };
};

export default function ColumnAccessManagementModal({ open, onClose, projectId }) {
  const { theme, langMode } = useContext(AppContext);
  const t = translations[langMode] || translations.en;

  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState(null);

  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [loadingColumns, setLoadingColumns] = useState(false);

  const [savedGrant, setSavedGrant] = useState(null);
  const [checkedKeys, setCheckedKeys] = useState(new Set());
  const [loadingGrant, setLoadingGrant] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");

  const userTypeOptions = useMemo(
    () => USER_TYPE_VALUES.map((value) => ({ value, label: t?.[value] ?? value })),
    [t]
  );
  const selectorPortalTarget = typeof document !== "undefined" ? document.body : undefined;
  const selectorOverlayStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 10100 }),
      menu: (base) => ({ ...base, zIndex: 10100 }),
    }),
    []
  );

  const allColumns = useMemo(() => (Array.isArray(dynamicColumns) ? dynamicColumns : []), [dynamicColumns]);
  const savedColumnKeys = useMemo(() => (Array.isArray(savedGrant?.columnKeys) ? savedGrant.columnKeys : []), [savedGrant]);
  const savedColumnKeySet = useMemo(() => new Set(savedColumnKeys), [savedColumnKeys]);
  const checkedColumnKeySet = useMemo(
    () => new Set(allColumns.filter((column) => checkedKeys.has(column.id)).map((column) => column.key)),
    [allColumns, checkedKeys]
  );

  const currentlyAccessibleChips = useMemo(
    () =>
      savedColumnKeys.map((key, index) => {
        const match = allColumns.find((column) => column.key === key);
        return {
          id: `saved|${key}|${index}`,
          key,
          label: match?.label ?? key,
          group: match?.group ?? "details",
        };
      }),
    [savedColumnKeys, allColumns]
  );

  const groupedColumns = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = !query
      ? allColumns
      : allColumns.filter(
          (column) =>
            String(column.label || "").toLowerCase().includes(query) ||
            String(column.key || "").toLowerCase().includes(query)
        );
    return {
      standard: filtered.filter((column) => column.group === "standard"),
      details: filtered.filter((column) => column.group !== "standard"),
    };
  }, [allColumns, search]);

  const totalColumnsCount = allColumns.length;
  const selectedColumnsCount = checkedKeys.size;
  const workflowStepLabel = t?.workflowStepLabel ?? `1. ${t?.selectWorkflowLabel ?? "Select workflow"}`;
  const userTypeStepLabel = t?.userTypeStepLabel ?? `2. ${t?.selectUserTypeLabel ?? "Select user type"}`;
  const currentlyAccessibleTitle = applyTemplate(
    t?.currentlyAccessibleCountLabel ?? "{label} - {userType} ({count})",
    {
      label: t?.currentlyAccessible ?? "Currently Accessible",
      userType: selectedUserType?.label ?? "",
      count: currentlyAccessibleChips.length,
    }
  );
  const manageAccessTitle = applyTemplate(
    t?.manageAccessCountLabel ?? "{label} - {selected} {of} {total} {selectedLabel}",
    {
      label: t?.manageAccessLabel ?? "Manage Access",
      selected: selectedColumnsCount,
      of: t?.of ?? "of",
      total: totalColumnsCount,
      selectedLabel: t?.selected ?? "selected",
    }
  );
  const fetchSelectedGrant = useCallback(async (workflowId, userType) => {
    const rows = await getColumnAccessList({ workflowId, userType });
    return (rows || []).find((row) => row.userType === userType) || null;
  }, []);

  useEffect(() => {
    if (open) return;
    setSelectedWorkflow(null);
    setSelectedUserType(null);
    setDynamicColumns([]);
    setSavedGrant(null);
    setCheckedKeys(new Set());
    setSearch("");
    setDeleteConfirmOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open || projectId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getActivityListByProjectId(projectId);
        if (!cancelled) setWorkflows(getPermittedActivities(Array.isArray(data) ? data : []));
      } catch (error) {
        console.error("Error fetching workflow list:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  useEffect(() => {
    if (!open || !selectedWorkflow?.value || !selectedUserType?.value) {
      setDynamicColumns([]);
      return;
    }
    let cancelled = false;
    setLoadingColumns(true);
    (async () => {
      try {
        const keys = await getColumnOptionsForAccess({
          workflowId: selectedWorkflow.value,
          userType: selectedUserType.value,
        });
        if (cancelled) return;
        const fields = (Array.isArray(keys) ? keys : [])
          .map((item, index) => extractColumnEntry(item, index, t))
          .filter(Boolean);
        setDynamicColumns(fields);
      } catch (error) {
        console.error("Error fetching workflow columns:", error);
        if (!cancelled) setDynamicColumns([]);
      } finally {
        if (!cancelled) setLoadingColumns(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedWorkflow, selectedUserType, t]);

  useEffect(() => {
    if (!open || !selectedWorkflow?.value || !selectedUserType?.value) {
      setSavedGrant(null);
      setCheckedKeys(new Set());
      return;
    }
    let cancelled = false;
    setLoadingGrant(true);
    (async () => {
      try {
        const match = await fetchSelectedGrant(selectedWorkflow.value, selectedUserType.value);
        if (cancelled) return;
        setSavedGrant(match);
      } catch (error) {
        console.error("Error fetching column access grant:", error);
        if (!cancelled) {
          setSavedGrant(null);
          setCheckedKeys(new Set());
        }
      } finally {
        if (!cancelled) setLoadingGrant(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedWorkflow, selectedUserType, fetchSelectedGrant]);

  useEffect(() => {
    if (!savedGrant) return;
    const savedSet = new Set(savedGrant.columnKeys ?? []);
    setCheckedKeys(new Set(allColumns.filter((column) => savedSet.has(column.key)).map((column) => column.id)));
  }, [savedGrant, allColumns]);

  const toggleColumn = (columnId) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      return next;
    });
  };

  const canManage = Boolean(selectedWorkflow?.value && selectedUserType?.value);

  const handleSelectAll = () => setCheckedKeys(new Set(allColumns.map((c) => c.id)));
  const handleClear = () => setCheckedKeys(new Set());
  const handleDefault = () => {
    const savedSet = new Set(savedGrant?.columnKeys ?? []);
    setCheckedKeys(new Set(allColumns.filter((column) => savedSet.has(column.key)).map((column) => column.id)));
  };

  const handleSave = async () => {
    if (!canManage || saving) return;
    setSaving(true);
    try {
      const columnKeys = Array.from(
        new Set(
          allColumns
            .filter((column) => checkedKeys.has(column.id))
            .map((column) => column.key)
        )
      );
      if (savedGrant?.taskColumnAccessId) {
        await updateColumnAccess(savedGrant.taskColumnAccessId, columnKeys);
      } else {
        await grantColumnAccess({
          workflowId: selectedWorkflow.value,
          userTypes: [selectedUserType.value],
          columnKeys,
        });
      }
      const refreshed = await fetchSelectedGrant(selectedWorkflow.value, selectedUserType.value);
      setSavedGrant(refreshed);
      const refreshedSet = new Set(refreshed?.columnKeys ?? []);
      setCheckedKeys(new Set(allColumns.filter((column) => refreshedSet.has(column.key)).map((column) => column.id)));
      toast.success(t?.columnAccessSaved ?? "Column access saved");
    } catch (error) {
      console.error("Error saving column access:", error);
      toast.error(
        error?.response?.data?.body?.value ||
          error?.response?.data?.body?.error ||
          error?.response?.data?.header?.message ||
          t?.erroccureduringtask ||
          "Something went wrong"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!savedGrant?.taskColumnAccessId || deleting) return;
    setDeleting(true);
    try {
      const deleteMessage = await deleteColumnAccess(savedGrant.taskColumnAccessId);
      setDeleteConfirmOpen(false);
      setSavedGrant(null);
      setCheckedKeys(new Set());

      try {
        const refreshed = await fetchSelectedGrant(selectedWorkflow.value, selectedUserType.value);
        setSavedGrant(refreshed);
        const refreshedSet = new Set(refreshed?.columnKeys ?? []);
        setCheckedKeys(new Set(allColumns.filter((column) => refreshedSet.has(column.key)).map((column) => column.id)));
      } catch (refreshError) {
        console.error("Error refreshing grant after delete:", refreshError);
      }

      const successMessage =
        (typeof deleteMessage === "string" && deleteMessage.trim()) ||
        t?.columnAccessDeleted ||
        "Column access removed";
      toast.success(successMessage);
    } catch (error) {
      console.error("Error deleting column access:", error);
      toast.error(
        (typeof error?.response?.data?.body?.value === "string" &&
          error?.response?.data?.body?.value.trim()) ||
          (typeof error?.response?.data?.body?.error === "string" &&
            error?.response?.data?.body?.error.trim()) ||
          (typeof error?.response?.data?.header?.message === "string" &&
            error?.response?.data?.header?.message.trim()) ||
          t?.erroccureduringtask ||
          "Something went wrong"
      );
    } finally {
      setDeleting(false);
    }
  };

  const removingKeys = useMemo(() => {
    return new Set(
      allColumns
        .filter((column) => savedColumnKeySet.has(column.key) && !checkedColumnKeySet.has(column.key))
        .map((column) => column.id)
    );
  }, [allColumns, savedColumnKeySet, checkedColumnKeySet]);

  const addingKeys = useMemo(() => {
    return new Set(
      allColumns
        .filter((column) => !savedColumnKeySet.has(column.key) && checkedColumnKeySet.has(column.key))
        .map((column) => column.id)
    );
  }, [allColumns, savedColumnKeySet, checkedColumnKeySet]);

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[10000] flex items-end justify-center p-0 sm:items-center sm:p-4 md:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel
                className={`relative flex h-[100dvh] w-full transform flex-col overflow-hidden rounded-none border shadow-2xl transition-all sm:h-[88vh] sm:max-h-[92vh] sm:max-w-5xl sm:rounded-2xl
                  ${theme === "dark"
                    ? `bg-gray-800 text-white ${deleteConfirmOpen ? "border-transparent" : "border-gray-700"}`
                    : theme === "high-contrast"
                      ? "bg-black text-yellow-300 border border-yellow-300"
                      : `bg-white text-slate-800 ${deleteConfirmOpen ? "border-transparent" : "border-slate-100"}`
                  }`}
              >
                <div className="flex items-start justify-between border-b border-slate-100 px-4 py-4 sm:px-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-800">
                      {t?.columnAccessManagementTitle ?? "Column Access Management"}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {t?.columnAccessManagementSubtitle ??
                        "Control which columns each user type can see, per workflow"}
                    </p>
                  </div>
                  <ModalCloseButton onClick={onClose} ariaLabel="Close modal" />
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700">
                        {workflowStepLabel} <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1">
                        <ReactSelector
                          dataTestId="column-access-workflow-select"
                          options={workflows.map((w) => ({ value: w.workflowId, label: w.workflowName }))}
                          value={selectedWorkflow}
                          onChange={(opt) => setSelectedWorkflow(opt)}
                          placeholder={t?.selectWorkflowPlaceholder ?? t?.selectWorkflowLabel ?? "Select workflow"}
                          height="42px"
                          menuPortalTarget={selectorPortalTarget}
                          menuPosition="fixed"
                          styles={selectorOverlayStyles}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">
                        {userTypeStepLabel} <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-1">
                        <ReactSelector
                          dataTestId="column-access-usertype-select"
                          options={userTypeOptions}
                          value={selectedUserType}
                          onChange={(opt) => setSelectedUserType(opt)}
                          placeholder={t?.selectUserTypePlaceholder ?? t?.selectUserTypeLabel ?? "Select user type"}
                          height="42px"
                          menuPortalTarget={selectorPortalTarget}
                          menuPosition="fixed"
                          styles={selectorOverlayStyles}
                        />
                      </div>
                    </div>
                  </div>

                  {!canManage ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 min-h-[44vh] sm:min-h-[420px] flex items-center justify-center px-6">
                      <div className="text-center max-w-lg">
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#EEF6FF]">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <rect x="4" y="4" width="7" height="7" rx="1.5" stroke="#1E63B6" strokeWidth="1.8" />
                            <rect x="13" y="13" width="7" height="7" rx="1.5" stroke="#1E63B6" strokeWidth="1.8" />
                            <path d="M11 8H13V13" stroke="#1E63B6" strokeWidth="1.8" strokeLinecap="round" />
                          </svg>
                        </div>
                        <p className="text-[12px] sm:text-[14px] leading-tight font-semibold tracking-[-0.01em] text-[#0F2A52] mb-2.5">
                          {t?.noWorkflowSelectedTitle ?? "No workflow selected"}
                        </p>
                        <p className="text-[10px] sm:text-[12px] leading-6 font-normal text-[#6B7FA3]">
                          {t?.selectWorkflowAndUserTypePrompt ??
                            "Pick a workflow, then a user type, to view and manage column access."}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                          {currentlyAccessibleTitle}
                        </p>
                        {loadingGrant ? (
                          <p className="text-sm text-slate-400">{t?.Processing ?? "Loading..."}</p>
                        ) : currentlyAccessibleChips.length ? (
                          <div className="flex flex-wrap gap-2">
                            {currentlyAccessibleChips.map((column) => (
                              <span
                                key={column.id}
                                className="rounded-full bg-blue-50 text-[#236DB4] text-xs font-medium px-3 py-1"
                              >
                                {column.label}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400">{t?.noResults ?? "No columns granted yet"}</p>
                        )}
                      </div>

                      <div className="border border-slate-200 rounded-lg">
                        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 truncate min-w-0">
                            {manageAccessTitle}
                          </p>
                          <div className="flex items-center gap-2 text-xs shrink-0 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={handleSelectAll}
                              className="font-medium text-[#3157d5] hover:underline whitespace-nowrap"
                            >
                              {t?.columnsModalSelectAll ?? "Select all"}
                            </button>
                            <span className="text-slate-300">|</span>
                            <button
                              type="button"
                              onClick={handleDefault}
                              className="font-medium text-slate-500 hover:text-slate-700 hover:underline whitespace-nowrap"
                            >
                              {t?.columnsModalResetDefault ?? t?.default ?? "Default"}
                            </button>
                            <span className="text-slate-300">|</span>
                            <button
                              type="button"
                              onClick={handleClear}
                              className="font-medium text-slate-500 hover:text-slate-700 hover:underline whitespace-nowrap"
                            >
                              {t?.columnsModalClearAll ?? t?.clear ?? "Clear"}
                            </button>
                          </div>
                        </div>
                        <div className="px-3 py-2 border-b border-slate-100">
                          <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={
                              t?.columnsModalSearchPlaceholder ?? `Search ${totalColumnsCount} columns...`
                            }
                            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-[#236DB4] focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20"
                          />
                        </div>
                        {/* What these grants do and do not reach. The Excel
                            export honours dynamic-column access, but the
                            static system columns are written to every sheet
                            regardless of what is granted here -- worth saying
                            in the place the grants are made, not after the
                            fact when a sheet arrives with unexpected columns. */}
                        <div className="mx-3 mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-950/30">
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                          <p className="text-xs leading-5 text-amber-800 dark:text-amber-300">
                            <span className="font-semibold">
                              {t?.noteLabel ?? "Note:"}
                            </span>{" "}
                            {t?.columnAccessExportNote ??
                              "These grants apply to dynamic columns only. The Excel export follows dynamic-column access, but static system columns are always included in the exported file and cannot be granted or revoked here."}
                          </p>
                        </div>
                        <div className="task-list-scrollbar max-h-[42vh] overflow-y-auto p-3 sm:max-h-72">
                          {loadingColumns ? (
                            <p className="text-sm text-slate-400 py-4 text-center">
                              {t?.Processing ?? "Loading..."}
                            </p>
                          ) : groupedColumns.standard.length || groupedColumns.details.length ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {[
                                { key: "details", title: t?.detailsGroupLabel ?? "Dynamic Columns", rows: groupedColumns.details },
                                { key: "standard", title: t?.standardGroupLabel ?? "Static Columns", rows: groupedColumns.standard },
                              ].map((group) => (
                                <div key={group.key}>
                                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    {group.title} ({group.rows.length})
                                  </p>
                                  {group.rows.length ? (
                                    <div className="space-y-1">
                                      {group.rows.map((column) => {
                                        const checked = checkedKeys.has(column.id);
                                        const isRemoving = removingKeys.has(column.id);
                                        const isAdding = addingKeys.has(column.id);
                                        return (
                                          <label
                                            key={column.id}
                                            className="flex items-center gap-2 py-1.5 cursor-pointer"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={() => toggleColumn(column.id)}
                                              className="h-3.5 w-3.5 rounded border-slate-300 accent-[#236DB4]"
                                            />
                                            <span
                                              className={`text-sm ${isRemoving ? "line-through text-slate-400" : "text-slate-700"}`}
                                            >
                                              {column.label}
                                            </span>
                                            {isAdding && (
                                              <span className="text-[10px] font-medium text-green-700 bg-green-50 rounded px-1.5 py-0.5">
                                                {t?.newBadge ?? "New"}
                                              </span>
                                            )}
                                            {isRemoving && (
                                              <span className="text-[10px] font-medium text-red-600 bg-red-50 rounded px-1.5 py-0.5">
                                                {t?.removingBadge ?? "Removing"}
                                              </span>
                                            )}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-slate-400 py-2">
                                      {t?.noResults ?? "No fields found"}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-400 py-4 text-center">
                              {t?.noResults ?? "No fields found"}
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="border-t border-slate-100 px-4 py-3 sm:px-6 sm:py-4">
                  <div className="flex items-center justify-end gap-2 sm:gap-3">
                    {/* <CommonButton
                      onClick={() => setDeleteConfirmOpen(true)}
                      variant="danger"
                      disabled={!savedGrant?.taskColumnAccessId}
                      tooltipText={t?.deleteAccessBtn ?? "Delete Access"}
                      tooltipPosition="top"
                      className="!w-auto"
                    >
                      {t?.deleteAccessBtn ?? "Delete Access"}
                    </CommonButton> */}
                    <CommonButton colorIntent="success"
                      onClick={handleSave}
                      variant="primary"
                      disabled={!canManage || saving}
                      tooltipPosition="top"
                      className="!w-auto"
                    >
                      {saving ? (t?.Processing ?? "Saving...") : (t?.saveChangesBtn ?? "Save Changes")}
                    </CommonButton>
                  </div>
                </div>

                {deleteConfirmOpen && (
                  <DeletePermission
                    isLoading={deleting}
                    zIndexClass="z-[11000]"
                    onClose={() => setDeleteConfirmOpen(false)}
                    deleteFunction={handleDelete}
                    onDeleteText={`${t?.confirmDeleteColumnAccess ?? "Delete the saved column access for"} ${
                      selectedUserType?.label ?? ""
                    }?`}
                  />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
